/**
 * Seed payments with deterministic IDs.
 *
 * These exist so that mockRefunds and mockDisputes can reference real
 * payment records via their `paymentId` field. Each entry maps to a
 * real customer in mockCustomers and to one or more refunds/disputes.
 *
 * IDs kept in UUID format to match the hardcoded paymentId values that
 * were already written in mockRefunds / mockDisputes.
 */
import type { Payment } from '../types/payment.types'
import { mockCustomers } from './mockCustomers'

type CustRef = (typeof mockCustomers)[number]

function cRef(id: string): CustRef {
  const c = mockCustomers.find((c) => c.id === id)
  if (!c) throw new Error(`Seed payment references unknown customer: ${id}`)
  return c
}

type SeedArgs = {
  id: string
  cust: CustRef
  amount: number
  currency?: string
  status: Payment['status']
  paymentMethod: { displayString: string; last4: string; brand: string; type?: string }
  createdAt: string
  description?: string
}

function seed({
  id,
  cust,
  amount,
  currency = 'BGN',
  status,
  paymentMethod,
  createdAt,
  description = 'Payment',
}: SeedArgs): Payment {
  const amt = { amount, decimal: 2, currency }
  return {
    id,
    amount: amt,
    amounts: {
      original: amt,
      approved: amt,
      cashback: { amount: 0, decimal: 2, currency },
      billing: amt,
    },
    currency,
    status,
    description,
    customer: { id: cust.id, name: cust.name, email: cust.email },
    paymentMethod: {
      type: paymentMethod.type ?? 'card',
      displayString: paymentMethod.displayString,
      last4: paymentMethod.last4,
      brand: paymentMethod.brand,
    },
    createdAt,
    timestamps: { initiated: createdAt, authorized: createdAt },
    transactionRef: id.replace(/-/g, '').slice(0, 12).toUpperCase(),
    account: { accountId: 'ACC-MAIN01', name: 'Barracuda Main Account', email: 'ops@barracuda.io' },
    metadata: {},
    lastUpdated: new Date(createdAt),
    channel: 'online',
    channelId: 'WEB-SEED',
    eligibility: 'eligible',
    store: 'Online Store',
    declineReason: '',
    riskScore: String(cust.riskScore),
    merchant: 'Barracuda',
    program: 'Standard',
    organization: 'ORG-MAIN',
    bank: 'BNK-MAIN',
    productPlatform: 'online',
    processor: 'stripe',
  }
}

// ── Customers ────────────────────────────────────────────────────────────────
const c001 = cRef('cust_001') // John Doe
const c002 = cRef('cust_002') // Jane Smith
const c003 = cRef('cust_003') // Bob Wilson
const c004 = cRef('cust_004') // Alice Johnson
const c005 = cRef('cust_005') // Charlie Brown
const c006 = cRef('cust_006') // Emma Davis
const c007 = cRef('cust_007') // Michael Rodriguez
const c008 = cRef('cust_008') // Sarah Thompson
const c009 = cRef('cust_009') // David Kim
const c010 = cRef('cust_010') // Lisa Wang
const c011 = cRef('cust_011') // Oliver Bennett
const c012 = cRef('cust_012') // Lena Hoffmann
const c013 = cRef('cust_013') // Yuki Tanaka
const c014 = cRef('cust_014') // Sofia Martins
const c015 = cRef('cust_015') // Priya Nair
const c016 = cRef('cust_016') // Marcus Osei
const c017 = cRef('cust_017') // Chloe Dupont
const c018 = cRef('cust_018') // Nathan Kowalski
const c019 = cRef('cust_019') // Amira Hassan
const c020 = cRef('cust_020') // Tom Nguyen

// ── Reusable payment methods ─────────────────────────────────────────────────
const PM = {
  visa4242:   { displayString: 'Visa ••• 4242',       last4: '4242', brand: 'Visa' },
  mc0036:     { displayString: 'Mastercard ••• 0036', last4: '0036', brand: 'Mastercard' },
  paypal:     { displayString: 'PayPal',              last4: '',     brand: 'PayPal',     type: 'paypal' },
  bankXfer:   { displayString: 'Bank Transfer',       last4: '',     brand: 'Bank Transfer', type: 'bank_transfer' },
  googlePay:  { displayString: 'Google Pay',          last4: '',     brand: 'Google Pay', type: 'google_pay' },
  applePay:   { displayString: 'Apple Pay',           last4: '',     brand: 'Apple Pay',  type: 'apple_pay' },
  sepa:       { displayString: 'SEPA Direct Debit',   last4: '',     brand: 'SEPA',       type: 'sepa_debit' },
  upi:        { displayString: 'UPI',                 last4: '',     brand: 'UPI',        type: 'upi' },
  mobileMoney:{ displayString: 'Mobile Money',        last4: '',     brand: 'Mobile Money', type: 'mobile_money' },
  visa1234:   { displayString: 'Visa ••• 1234',       last4: '1234', brand: 'Visa' },
  mc5678:     { displayString: 'Mastercard ••• 5678', last4: '5678', brand: 'Mastercard' },
}

// ── Seed payments ────────────────────────────────────────────────────────────
// Each payment ID matches one or more records in mockRefunds / mockDisputes.

export const mockSeedPayments: Payment[] = [
  // ─── Payments with both a refund AND a dispute ──────────────────────────
  seed({ id: '8a6e7a2d-0c91-4d44-bb0e-5cf4906c6a45', cust: c006, amount: 8000,  status: 'refunded',          paymentMethod: PM.mc0036,    createdAt: '2025-06-18T09:00:00Z', description: 'Subscription payment — Emma Davis' }),
  seed({ id: '0ff165ee-6035-47e0-849c-530302075189', cust: c002, amount: 500,   status: 'partially_refunded', paymentMethod: PM.paypal,    createdAt: '2025-06-19T14:00:00Z', description: 'Duplicate charge — Jane Smith' }),
  seed({ id: '12ba604c-703a-4cca-85c1-dc77fea3e365', cust: c005, amount: 1000,  status: 'succeeded',          paymentMethod: PM.visa4242,  createdAt: '2025-06-17T10:00:00Z', description: 'Flagged transaction — Charlie Brown' }),
  seed({ id: '23ba9cc9-3350-46cb-88e6-7027f669342c', cust: c010, amount: 5000,  status: 'refunded',           paymentMethod: PM.visa4242,  createdAt: '2025-06-17T10:00:00Z', description: 'Product return — Lisa Wang' }),
  seed({ id: '0541af14-f40c-4c63-8120-3de24687501f', cust: c004, amount: 1000,  status: 'succeeded',          paymentMethod: PM.visa4242,  createdAt: '2025-06-17T10:00:00Z', description: 'Subscription charge — Alice Johnson' }),
  seed({ id: '3d45e9ac-9c9f-4208-ab08-2bb6228ada27', cust: c014, amount: 10000, status: 'partially_refunded', paymentMethod: PM.visa4242,  createdAt: '2025-06-17T10:00:00Z', description: 'Damaged goods — Sofia Martins' }),
  seed({ id: 'ed600cf8-3d7e-477f-9986-0afe0c909a33', cust: c003, amount: 1200,  status: 'refunded',           paymentMethod: PM.bankXfer,  createdAt: '2025-06-17T10:00:00Z', description: 'Enterprise license — Bob Wilson' }),
  seed({ id: '237ce88c-758e-4048-9ace-756486929296', cust: c013, amount: 12541, status: 'refunded',           paymentMethod: PM.visa4242,  createdAt: '2025-06-17T10:00:00Z', description: 'Annual subscription — Yuki Tanaka' }),
  seed({ id: 'df83920d-38b9-457e-8034-7b5e0e92dd3a', cust: c007, amount: 5000,  status: 'refund_processing',  paymentMethod: PM.googlePay, createdAt: '2025-06-17T10:00:00Z', description: 'Mobile subscription — Michael Rodriguez' }),
  seed({ id: '6f2aa264-b240-4e87-8ac5-f72ee400d643', cust: c001, amount: 2000,  status: 'refunded',           paymentMethod: PM.visa4242,  createdAt: '2025-06-17T10:00:00Z', description: 'Student plan — John Doe' }),

  // ─── Refund-only payment ────────────────────────────────────────────────
  seed({ id: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890', cust: c008, amount: 3500,  status: 'succeeded',          paymentMethod: PM.visa4242,  createdAt: '2025-06-20T08:00:00Z', description: 'Product purchase — Sarah Thompson' }),

  // ─── Dispute-only payments ──────────────────────────────────────────────
  seed({ id: 'fe4b2176-f496-4398-a633-84a81e76394f', cust: c009, amount: 1000,  status: 'succeeded',          paymentMethod: PM.visa1234,  createdAt: '2025-06-26T12:00:00Z', description: 'Restaurant supply order — David Kim' }),
  seed({ id: 'a4c49a44-e5ec-49c2-81fb-02f59518714f', cust: c011, amount: 100,   status: 'succeeded',          paymentMethod: PM.visa4242,  createdAt: '2025-06-27T14:00:00Z', description: 'Card verification — Oliver Bennett' }),
  seed({ id: '8ae06527-f5f4-43d5-8836-8f6d5fd04aff', cust: c012, amount: 100,   status: 'succeeded',          paymentMethod: PM.sepa,      createdAt: '2025-06-28T10:00:00Z', description: 'Authorisation check — Lena Hoffmann' }),
  seed({ id: '49bc4429-e793-4bb5-9db3-902ee0e72fd9', cust: c015, amount: 950,   status: 'succeeded',          paymentMethod: PM.upi,       createdAt: '2025-06-30T08:00:00Z', description: 'IT services subscription — Priya Nair' }),
  seed({ id: '01e197c9-662b-42c8-a89c-82f6999149d3', cust: c018, amount: 5000,  status: 'succeeded',          paymentMethod: PM.visa4242,  createdAt: '2025-07-02T13:00:00Z', description: 'Software license — Nathan Kowalski' }),
  seed({ id: 'e05c8ed0-8e50-4c27-af55-2f00f52c997b', cust: c016, amount: 5000,  status: 'succeeded',          paymentMethod: PM.mobileMoney, createdAt: '2025-07-03T15:00:00Z', description: 'Logistics services — Marcus Osei' }),
  seed({ id: '12add6bb-2c2b-477c-adb0-c46dad366ff4', cust: c017, amount: 100,   status: 'succeeded',          paymentMethod: PM.sepa,      createdAt: '2025-07-04T09:00:00Z', description: 'Design retainer verification — Chloe Dupont' }),
  seed({ id: '78b1e4c5-079c-4389-b7b1-ef27c25254aa', cust: c019, amount: 5000,  status: 'succeeded',          paymentMethod: PM.visa4242,  createdAt: '2025-07-06T12:00:00Z', description: 'Pharma distribution order — Amira Hassan' }),
  seed({ id: '1a054460-487b-4bdd-af5e-e299126252cf', cust: c020, amount: 4000,  status: 'succeeded',          paymentMethod: PM.applePay,  createdAt: '2025-07-07T16:00:00Z', description: 'Marketing campaign — Tom Nguyen' }),
  seed({ id: 'a7f7c1ec-ef29-43e0-833f-359c268a4f36', cust: c009, amount: 4000,  status: 'succeeded',          paymentMethod: PM.mc5678,    createdAt: '2025-07-08T10:00:00Z', description: 'Catering order — David Kim' }),
  seed({ id: 'e06781fd-1551-45ee-bdcf-79bd97c8c22a', cust: c011, amount: 100,   status: 'succeeded',          paymentMethod: PM.visa4242,  createdAt: '2025-07-09T11:00:00Z', description: 'Repeat verification — Oliver Bennett' }),
  seed({ id: '6ac51fe2-09d6-4bc7-bb7d-234634480344', cust: c012, amount: 5000,  status: 'succeeded',          paymentMethod: PM.sepa,      createdAt: '2025-07-10T13:00:00Z', description: 'SaaS subscription — Lena Hoffmann' }),
  seed({ id: '1237012e-bd75-4ef4-be7f-6660d50656d2', cust: c015, amount: 5000,  status: 'succeeded',          paymentMethod: PM.upi,       createdAt: '2025-07-11T08:00:00Z', description: 'Consulting services — Priya Nair' }),
  seed({ id: 'a85d4e0e-89bf-4157-b928-f259f388bb26', cust: c018, amount: 5000,  status: 'succeeded',          paymentMethod: PM.visa4242,  createdAt: '2025-07-12T15:00:00Z', description: 'Development contract — Nathan Kowalski' }),
]
